import { InvalidUrlError } from '../../errors/errors';
import { BaseGQLService } from '../../services/BaseGQLService';
import { CursorInfo, PaginationResult } from '../../types/pagination';
import { ModelType } from '../../utils/GQLIdEncoder';
import { CallDetailMapper } from '../mappers/CallDetailMapper';
import { CallMapper } from '../mappers/CallMapper';
import { Call } from '../models/Call';
import { CallDetail } from '../models/CallDetail';

export const CALLS_FRAGMENT = `
totalCount
pageInfo {
  hasNextPage
  hasPreviousPage
  startCursor
  endCursor
}
edges {
  node {
    id
    timestamp
    isKeptByFilters
  }
}
`;

export const GET_CALLS_QUERY = (variant: ModelType) => `
query (
  $variantId: ID!,
  $first: Int,
  $last: Int,
  $after: String,
  $before: String,
) {
  node(id: $variantId) {
    ... on ${variant} {
      calls(
        first: $first,
        last: $last,
        after: $after,
        before: $before,
      ) {
        ${CALLS_FRAGMENT}
      }
    }
  }
}
`;

export const GET_CALL_DETAIL_QUERY = `
query($callId: ID!) {
  node(id: $callId) {
    ... on Call {
      arguments
      stackTrace {
        edges {
          node {
            class
            method
            line
            file
          }
        }
      }
    }
  }
}
`;

export class CallRepository extends BaseGQLService {
  private readonly callMapper = new CallMapper();
  private readonly callDetailMapper = new CallDetailMapper();

  async getCalls(variant: ModelType, variantId: number, cursor: CursorInfo): Promise<PaginationResult<Call>> {
    return this.query(
      GET_CALLS_QUERY(variant),
      {
        variantId: this.gqlIdEncoder.encode(variant, variantId),
        ...this.mapCursorInfo(cursor),
      },
      (data) => {
        if (!data.node) {
          throw new InvalidUrlError(`Invalid request: could not find ${variant} ${variantId}`);
        }
        const { edges, pageInfo, totalCount } = data.node.calls;
        return {
          data: edges.map(({ node }: any) => this.callMapper.mapGQLModel(node)),
          pageInfo,
          totalCount,
        };
      }
    );
  }

  async getCallDetail(callId: number): Promise<CallDetail> {
    return this.query(
      GET_CALL_DETAIL_QUERY,
      {
        callId: this.gqlIdEncoder.encode(ModelType.Call, callId),
      },
      (data) => {
        if (!data.node) {
          throw new InvalidUrlError(`Invalid request: could not find Call ${callId}`);
        }
        return this.callDetailMapper.mapGQLModel(data.node);
      }
    );
  }
}
